import { apiClient } from '@/services/api/client';

import { wafProtected } from './waf';

/** Campaign parameters the signup arrived with; the server keeps only the keys it knows. */
export type SignupAttribution = Record<string, string>;

export interface SignupBody {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  /** Consent to the privacy policy; the server refuses the signup without it. */
  accept_terms: boolean;
  /** Consent to the cookie policy, recorded with the signup. */
  cookie_consent?: boolean;
  /** Opt-in to marketing email, recorded with the signup. */
  marketing_consent?: boolean;
  /** Where the starter area of interest is drawn; the server uses the IP's country without it. */
  country?: string;
  attribution?: SignupAttribution;
}

/** Where a signup has got to. Mirrors `account.consts.SignupState`. */
export type SignupState = 'pending' | 'promoted' | 'expired';

export interface SignupStatus {
  state: SignupState;
  /** The project the app opens on; sent only with a session. */
  collection_id?: number;
}

export interface PasswordCheck {
  valid: boolean;
  errors: string[];
}

export interface OnboardingState {
  completed: boolean;
  /** Only free-tier users are asked the onboarding questions. */
  is_free_tier: boolean;
  can_rename_organisation: boolean;
  org_name: string;
  suggested_org_name: string;
  display_name: string;
  job_title: string;
  phone: string;
  /** Signup country (picked, else GeoIP, else the default). */
  country_code: string;
}

/** The account after a save. */
export type OnboardingSaved = Pick<
  OnboardingState,
  'completed' | 'org_name' | 'display_name' | 'job_title' | 'phone'
>;

export interface OnboardingBody {
  org_name?: string;
  job_title?: string;
  display_name?: string;
  /** National-form number; the server pairs it with `phone_country` to store E.164. */
  phone?: string;
  phone_country?: string;
}

export const signupApi = {
  /** Bot-checked by the WAF, which may ask for a CAPTCHA first. */
  async signup(body: SignupBody): Promise<{ detail: string }> {
    return wafProtected('signup', (headers) =>
      apiClient.post('/api/account/signup/', body, { headers })
    );
  },

  async resend(): Promise<{ detail: string }> {
    return wafProtected('signup_resend', (headers) =>
      apiClient.post('/api/account/signup/resend/', {}, { headers })
    );
  },

  /** Polled by the tab that started the signup. Read-only. */
  async status(): Promise<SignupStatus> {
    return apiClient.get('/api/account/signup/status/');
  },

  /** Sign this browser in to the account its signup became. */
  async claim(): Promise<SignupStatus> {
    return apiClient.post('/api/account/signup/claim/', {});
  },

  async verify(token: string): Promise<SignupStatus> {
    return apiClient.post('/api/account/signup/verify/', { token });
  },

  async verifyCode(code: string): Promise<SignupStatus> {
    return apiClient.post('/api/account/signup/verify/code/', { code });
  },

  async checkPassword(password: string, signal?: AbortSignal): Promise<PasswordCheck> {
    return apiClient.post('/api/account/signup/password/check/', { password }, { signal });
  },

  async onboarding(): Promise<OnboardingState> {
    return apiClient.get('/api/account/onboarding/');
  },

  /** `completed` is true only once the account has a job title and a phone number. */
  async completeOnboarding(body: OnboardingBody): Promise<OnboardingSaved> {
    return apiClient.post('/api/account/onboarding/', body);
  },

  /** Settings page: every field sent is saved as sent, so "" clears it. */
  async updateProfile(body: OnboardingBody): Promise<OnboardingSaved> {
    return apiClient.patch('/api/account/onboarding/', body);
  },
};
